import { useEffect, useRef, useState } from "react";
import { Camera, X, Check, RefreshCw, Trash2 } from "lucide-react";
import { drawStamp } from "@/lib/media";

export function CameraCapture({ open, onClose, onCapture, stamp = true }) {
  const videoRef = useRef();
  const streamRef = useRef();
  const fallbackRef = useRef();
  const [shots, setShots] = useState([]);
  const [facing, setFacing] = useState("environment");
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr(false);
    let active = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
        if (!active) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch { setErr(true); }
    })();
    return () => { active = false; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [open, facing]);

  if (!open) return null;

  const stop = () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  const clearShots = () => setShots((p) => { p.forEach((s) => URL.revokeObjectURL(s.url)); return []; });
  const close = () => { stop(); clearShots(); onClose(); };

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0);
    if (stamp) drawStamp(ctx, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      setShots((p) => [...p, { file, url: URL.createObjectURL(blob) }]);
    }, "image/jpeg", 0.9);
  };

  const done = () => { const files = shots.map((s) => s.file); stop(); clearShots(); onCapture(files); onClose(); };

  const onFallback = (e) => {
    const files = Array.from(e.target.files || []);
    clearShots();
    onClose();
    if (files.length) onCapture(files);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black flex flex-col" data-testid="camera-modal">
      <div className="flex items-center justify-between p-4 text-white">
        <div className="font-head font-black uppercase flex items-center gap-2">
          <Camera size={18} className="text-[#FF5722]" /> Camera
          {stamp && <span className="font-mono text-[10px] text-[#FF5722] border border-[#FF5722]/40 px-1.5 py-0.5">TIMESTAMP</span>}
        </div>
        <button data-testid="camera-close" onClick={close} className="text-zinc-300 hover:text-white"><X size={24} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center bg-zinc-950 relative overflow-hidden">
        {err ? (
          <div className="text-center text-zinc-300 p-6">
            <Camera size={48} className="mx-auto text-zinc-600" />
            <p className="mt-3 font-mono text-sm">Live camera unavailable here.<br />Use your device camera instead.</p>
            <input ref={fallbackRef} type="file" accept="image/*" capture="environment" multiple onChange={onFallback} className="hidden" data-testid="camera-fallback-input" />
            <button onClick={() => fallbackRef.current?.click()} className="mt-4 bg-[#FF5722] text-black border-2 border-black font-head font-bold uppercase text-sm px-4 py-2">Open device camera</button>
          </div>
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="max-h-full max-w-full" />
        )}
      </div>
      {shots.length > 0 && (
        <div className="flex gap-2 p-2 overflow-x-auto bg-zinc-900" data-testid="camera-shots">
          {shots.map((s, i) => (
            <div key={s.url} className="relative shrink-0">
              <img src={s.url} alt="" className="w-16 h-16 object-cover border-2 border-zinc-700" />
              <button onClick={() => setShots((p) => { const s = p[i]; if (s) URL.revokeObjectURL(s.url); return p.filter((_, idx) => idx !== i); })} className="absolute -top-1 -right-1 bg-red-600 text-white border border-black p-0.5"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
      {!err && (
        <div className="flex items-center justify-center gap-6 p-5 bg-black">
          <button data-testid="camera-flip" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} className="text-zinc-300 hover:text-white p-3 border-2 border-zinc-700"><RefreshCw size={20} /></button>
          <button data-testid="camera-snap" aria-label="Take photo" onClick={snap} className="w-16 h-16 rounded-full bg-[#FF5722] border-4 border-white shadow-[0_0_0_2px_#000] active:scale-95 transition-transform" />
          <button data-testid="camera-done" onClick={done} disabled={shots.length === 0} className="text-black bg-[#FF5722] border-2 border-black font-head font-bold uppercase text-sm px-4 py-3 disabled:opacity-40 flex items-center gap-1"><Check size={18} /> {shots.length > 0 ? `Use ${shots.length}` : "Use"}</button>
        </div>
      )}
    </div>
  );
}
